import React, { useEffect, useMemo } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import {
	Accordion,
	ButtonStandard,
	Icon,
	SingleSelectInput,
	Skeleton,
	Text,
	Tooltip,
} from "@performyard/py-components";
import type { PatchSettingsRequest, SettingsResponse } from "@performyard/service-contracts";

import { useAdminSettingsHomeSettings, useUpdateAdminSettingsHomeSettings } from "../../../../api/hooks";
import useNavigate from "../../../../hooks/useNavigate";
import { useDirtyForm } from "../hooks/useDirtyForm";

import { ADMIN_HOME_ACCORDION_CLASS_NAME, ADMIN_HOME_BUTTON_CLASS_NAME } from "./adminHomeStyles";

type SettingValue = boolean;
type Values = Record<string, SettingValue | string | number>;

type ScalarControl = SettingsResponse["scalarControls"][number];

const MICROSOFT_TEAMS_SETUP_ROUTE = "/administration/microsoft-teams-setup";

const SettingLabel: React.FC<{ label: string; tooltip?: string }> = ({ label, tooltip }) => (
	<div className="flex items-center gap-1">
		<Text size="paragraph-2" dangerouslySetClassName="whitespace-nowrap font-semibold text-neutral-9">
			{label}
		</Text>
		{tooltip ? (
			<Tooltip content={tooltip}>
				<span className="inline-flex">
					<Icon icon={faCircleInfo} size={16} color="neutral-7" aria-label="More information" />
				</span>
			</Tooltip>
		) : null}
	</div>
);

type Props = { isOpen: boolean; onToggle: () => void; canEdit?: boolean; companyId?: string };

const CompanySettingsSection: React.FC<Props> = ({ isOpen, onToggle, canEdit = true, companyId }) => {
	const { data, isLoading } = useAdminSettingsHomeSettings(companyId);
	const navigate = useNavigate();

	const initial: Values = useMemo(() => {
		const out: Values = {};
		for (const s of data?.settings ?? []) out[s.key] = s.value;
		for (const c of data?.scalarControls ?? []) out[c.key] = c.value;
		return out;
	}, [data]);

	const { values, setField, discard, syncFromServer, isDirty, diff } = useDirtyForm<Values>(initial);

	useEffect(() => {
		if (data) syncFromServer(initial);
	}, [data, initial, syncFromServer]);

	const mutation = useUpdateAdminSettingsHomeSettings(undefined, companyId);

	const onSave = () => {
		const updates = Object.entries(diff)
			.filter(([, v]) => v !== undefined)
			.map(([key, value]) => ({
				key: key as PatchSettingsRequest["updates"][number]["key"],
				value,
			})) as PatchSettingsRequest["updates"];
		if (updates.length === 0) return;
		mutation.mutate({ updates });
	};

	const renderScalarControl = (control: ScalarControl) => (
		<div key={control.key} className="flex w-[288px] flex-col gap-2">
			<SettingLabel label={control.label} tooltip={control.tooltip} />
			<SingleSelectInput<string>
				value={String(values[control.key] ?? "")}
				options={control.options.map((option) => ({ value: String(option.value), label: option.label }))}
				onChange={(next) => {
					if (!next) return;
					setField(control.key, control.key === "defaultTimezone" ? next : Number(next));
				}}
				ariaLabel={`${control.label} status`}
				disabled={!canEdit}
			/>
		</div>
	);

	const hasAnyControls =
		!!data && (data.settings.length > 0 || data.scalarControls.length > 0 || data.microsoftTeamsSetup !== null);

	return (
		<Accordion
			isOpen={isOpen}
			onToggle={onToggle}
			variant="standard"
			className={ADMIN_HOME_ACCORDION_CLASS_NAME}
			headerContent={
				<div className="flex flex-col">
					<Text
						size="paragraph-1"
						dangerouslySetClassName="text-[18px] font-semibold leading-6 text-neutral-9"
					>
						Company Settings
					</Text>
					<Text size="paragraph-3" dangerouslySetClassName="leading-4 text-neutral-9">
						Set default company settings
					</Text>
				</div>
			}
		>
			{isLoading || !data ? (
				<Skeleton className="h-64" />
			) : !hasAnyControls ? (
				<div className="-mx-4 -my-3 p-8">
					<Text size="paragraph-2" dangerouslySetClassName="text-neutral-9">
						No company settings are available for this view.
					</Text>
				</div>
			) : (
				<div className="-mx-4 -my-3 flex flex-col gap-8 p-8">
					<div className="flex flex-col gap-4">
						{data.scalarControls.map(renderScalarControl)}
						{data.microsoftTeamsSetup ? (
							<div className="flex w-[288px] flex-col gap-2">
								<Text
									size="paragraph-2"
									dangerouslySetClassName="whitespace-nowrap font-semibold text-neutral-9"
								>
									{data.microsoftTeamsSetup.label}
								</Text>
								<ButtonStandard
									onClick={() => navigate(MICROSOFT_TEAMS_SETUP_ROUTE)}
									disabled={!canEdit}
									dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
								>
									{data.microsoftTeamsSetup.buttonLabel}
								</ButtonStandard>
							</div>
						) : null}
						{data.settings.map((s) => (
							<div key={s.key} className="flex w-[288px] flex-col gap-2">
								<SettingLabel label={s.label} tooltip={s.tooltip} />
								<SingleSelectInput<string>
									value={(values[s.key] ?? s.value) === true ? "true" : "false"}
									options={s.options.map((option) => ({
										value: option.value ? "true" : "false",
										label: option.label,
									}))}
									onChange={(next) => next && setField(s.key, next === "true")}
									ariaLabel={`${s.label} status`}
									disabled={!canEdit}
								/>
							</div>
						))}
					</div>
					<div className="flex items-center gap-2">
						<ButtonStandard
							onClick={onSave}
							disabled={!canEdit || !isDirty || mutation.isPending}
							dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
						>
							Save Changes
						</ButtonStandard>
						{isDirty ? (
							<ButtonStandard
								variant="outline"
								onClick={discard}
								dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
							>
								Discard
							</ButtonStandard>
						) : null}
					</div>
				</div>
			)}
		</Accordion>
	);
};

export default CompanySettingsSection;
